/**
 * ConservatoryCycle owns the Victorian glasshouse's Incubation/Irrigation
 * transition timing, lighting, shadow-zone expansion and events. Internal
 * dawn/day/dusk/midnight values remain compatibility vocabulary; canonical
 * gameplay maps Irrigation to day and Incubation to midnight.
 */
import { Scene } from "@babylonjs/core/scene";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { OVERGROWN_CONSERVATORY } from "../config/GameConfig";

export type TimeOfDay = "dawn" | "day" | "dusk" | "midnight";

/**
 * Canonical Conservatory phase vocabulary per 2026-04-14 companion book.
 * Maps onto the internal 4-phase light engine:
 *   - `irrigation` → `day` (cool misty stealth state)
 *   - `incubation` → `midnight` (hot dry predator state)
 *
 */
export type ConservatoryPhase = "incubation" | "irrigation";

const PHASE_ORDER: TimeOfDay[] = ["dawn", "day", "dusk", "midnight"];

/** After a cycle flip, reject further flips for this long (ms). */
const HYSTERESIS_LOCKOUT_MS = 700;

/** Default visual lerp duration for a canonical phase flip (ms). */
const CANONICAL_FLIP_DURATION_MS = 1800;

/** Map a canonical phase to the internal TimeOfDay used by the light engine. */
function canonicalToInternal(phase: ConservatoryPhase): TimeOfDay {
  return phase === "incubation" ? "midnight" : "day";
}

/** Map an internal TimeOfDay to the canonical phase. */
function internalToCanonical(time: TimeOfDay): ConservatoryPhase {
  return time === "midnight" || time === "dusk" ? "incubation" : "irrigation";
}

interface LightingPreset {
  ambient: Color3;
  directional: Color3;
  intensity: number;
}

/**
 * Shadow zone info tracked by the cycle for dynamic expansion.
 */
export interface ShadowZoneEntry {
  mesh: Mesh;
  baseScale: Vector3;
}

export class ConservatoryCycle {
  private currentPhase: TimeOfDay = "day";
  private transitioning = false;

  /** Scene lights controlled by the cycle. */
  private hemiLight: HemisphericLight;
  private dirLight: DirectionalLight;

  /** Registered shadow zones with their base (day) scales. */
  private shadowZones: ShadowZoneEntry[] = [];

  /** Transition animation state. */
  private transitionFrom: TimeOfDay | null = null;
  private transitionTo: TimeOfDay | null = null;
  private transitionElapsed = 0;
  private transitionDuration = 0;

  /**
   * Hysteresis lockout: after a canonical flip, further flip requests are
   * rejected for `HYSTERESIS_LOCKOUT_MS`. This prevents Basil from spamming
   * the master regulator and rage-quitting the escort puzzle mid-step.
   * Measured in ms of real delta accumulated via update().
   */
  private lockoutRemainingMs = 0;

  /** Precomputed lighting presets. */
  private presets: Record<TimeOfDay, LightingPreset>;

  constructor(_scene: Scene, hemiLight: HemisphericLight, dirLight: DirectionalLight) {
    this.hemiLight = hemiLight;
    this.dirLight = dirLight;

    // Build presets from config
    const cfg = OVERGROWN_CONSERVATORY.LIGHTING;
    this.presets = {
      dawn: {
        ambient: Color3.FromHexString(cfg.DAWN.ambient),
        directional: Color3.FromHexString(cfg.DAWN.directional),
        intensity: cfg.DAWN.intensity,
      },
      day: {
        ambient: Color3.FromHexString(cfg.DAY.ambient),
        directional: Color3.FromHexString(cfg.DAY.directional),
        intensity: cfg.DAY.intensity,
      },
      dusk: {
        ambient: Color3.FromHexString(cfg.DUSK.ambient),
        directional: Color3.FromHexString(cfg.DUSK.directional),
        intensity: cfg.DUSK.intensity,
      },
      midnight: {
        ambient: Color3.FromHexString(cfg.MIDNIGHT.ambient),
        directional: Color3.FromHexString(cfg.MIDNIGHT.directional),
        intensity: cfg.MIDNIGHT.intensity,
      },
    };

    // Apply initial lighting
    this.applyLighting(this.currentPhase);
  }

  // ─── Getters ──────────────────────────────────────────────────

  /** Internal 4-phase time value used by the lighting engine. */
  get phase(): TimeOfDay {
    return this.currentPhase;
  }

  /** Canonical 2-phase Conservatory state (spec §2.1). */
  get canonicalPhase(): ConservatoryPhase {
    return internalToCanonical(this.currentPhase);
  }

  get isIncubation(): boolean {
    return this.canonicalPhase === "incubation";
  }

  get isIrrigation(): boolean {
    return this.canonicalPhase === "irrigation";
  }

  get isNight(): boolean {
    return this.currentPhase === "midnight";
  }

  get isDaytime(): boolean {
    return this.currentPhase === "day" || this.currentPhase === "dawn";
  }

  get isTransitioning(): boolean {
    return this.transitioning;
  }

  /** True while the hysteresis lockout blocks a new flip request. */
  get isLockedOut(): boolean {
    return this.lockoutRemainingMs > 0;
  }

  /** Remaining lockout window in milliseconds; 0 when flips are allowed. */
  get lockoutMs(): number {
    return this.lockoutRemainingMs;
  }

  // ─── Shadow zone registration ─────────────────────────────────

  /**
   * Register shadow zone meshes so they can be scaled during transitions.
   * Call after creating shadow zones.
   */
  registerShadowZones(meshes: Mesh[]): void {
    this.shadowZones = meshes.map((mesh) => ({
      mesh,
      baseScale: mesh.scaling.clone(),
    }));
  }

  // ─── Phase transitions ────────────────────────────────────────

  /**
   * Advance to the next phase in the cycle (dawn -> day -> dusk -> midnight -> ...).
   */
  advancePhase(duration?: number): void {
    const idx = PHASE_ORDER.indexOf(this.currentPhase);
    const next = PHASE_ORDER[(idx + 1) % PHASE_ORDER.length];
    this.transitionToPhase(next, duration);
  }

  /**
   * Transition to a specific time of day. Internal-phase API retained for
   * the legacy DayNightTransition puzzle until its replacement lands.
   * New code should prefer `requestPhaseFlip` which enforces the canonical
   * phase vocabulary, the hysteresis lockout, and the `CONSERVATORY_*`
   * event pair.
   */
  transitionToPhase(target: TimeOfDay, duration?: number): void {
    if (this.transitioning || target === this.currentPhase) return;

    this.transitioning = true;
    this.transitionFrom = this.currentPhase;
    this.transitionTo = target;
    this.transitionElapsed = 0;
    this.transitionDuration = duration ?? OVERGROWN_CONSERVATORY.TIME.TRANSITION_DURATION;

    gameEvents.emit(GameEvents.DAY_NIGHT_TRANSITION, {
      from: this.currentPhase,
      to: target,
    });
  }

  /**
   * Canonical phase-flip request. Returns true if the flip was accepted
   * and started; false if rejected (already in target, transitioning, or
   * within the hysteresis lockout window). The caller is expected to
   * display an HUD toast like "regulator stabilizing" on rejection.
   *
   * On accept this fires BOTH event names (`CONSERVATORY_CYCLE_TRANSITION`
   * + legacy `DAY_NIGHT_TRANSITION`) so the cycle-aware NPCs and the
   * old escort puzzle both react. When the lerp completes the settled
   * event pair (`CONSERVATORY_PHASE_CHANGED` + `DAY_NIGHT_CHANGED`)
   * fires from `update()`.
   */
  requestPhaseFlip(target: ConservatoryPhase, duration?: number): boolean {
    if (this.lockoutRemainingMs > 0) return false;
    if (this.transitioning) return false;
    if (this.canonicalPhase === target) return false;

    const internalTarget = canonicalToInternal(target);
    const lerpDuration = duration ?? CANONICAL_FLIP_DURATION_MS;

    this.transitioning = true;
    this.transitionFrom = this.currentPhase;
    this.transitionTo = internalTarget;
    this.transitionElapsed = 0;
    this.transitionDuration = lerpDuration;
    this.lockoutRemainingMs = HYSTERESIS_LOCKOUT_MS;

    // Canonical event first (new listeners), then legacy alias for the
    // escort puzzle's fox-startle wiring that still subscribes to the
    // day/night event name. Payload shape differs — listeners should
    // pick whichever vocabulary they need.
    gameEvents.emit(GameEvents.CONSERVATORY_CYCLE_TRANSITION, {
      from: internalToCanonical(this.currentPhase),
      to: target,
      durationMs: lerpDuration,
    });
    gameEvents.emit(GameEvents.DAY_NIGHT_TRANSITION, {
      from: this.currentPhase,
      to: internalTarget,
    });

    return true;
  }

  /**
   * Commit immediately to a canonical phase — bypasses lockout + lerp.
   *
   * Intended for dev / QA priming after `forceSolvePuzzle(masterValve)`:
   * the real solve path relies on two player-driven valve-flip cycles,
   * but `forceSolvePuzzle` short-circuits that and the room would
   * otherwise stay in Incubation, blocking `nectarEscort`'s opossum
   * follow-chain. Calling `setPhaseImmediate('irrigation')` snaps the
   * room state so the forced-solve is internally consistent.
   *
   * Emits the canonical settled-event pair (`CONSERVATORY_PHASE_CHANGED`
   * + legacy `DAY_NIGHT_CHANGED`) so all downstream listeners react
   * exactly as if a normal transition had completed.
   *
   * Returns true if the phase changed; false if already at `target`.
   */
  setPhaseImmediate(target: ConservatoryPhase): boolean {
    if (this.canonicalPhase === target) return false;
    const internalTarget = canonicalToInternal(target);
    this.currentPhase = internalTarget;
    this.transitioning = false;
    this.transitionFrom = null;
    this.transitionTo = null;
    this.transitionElapsed = 0;
    this.lockoutRemainingMs = 0;
    // Apply final lighting/shadow state without running the update lerp.
    this.applyLightingBlend(internalTarget, internalTarget, 1);
    gameEvents.emit(GameEvents.CONSERVATORY_PHASE_CHANGED, {
      phase: target,
      isIncubation: target === "incubation",
    });
    gameEvents.emit(GameEvents.DAY_NIGHT_CHANGED, {
      phase: internalTarget,
      isNight: internalTarget === "midnight",
    });
    return true;
  }

  /**
   * Must be called each frame (pass deltaTime in milliseconds).
   */
  update(dtMs: number): void {
    // Tick the hysteresis lockout independently of the transition — we
    // want the lockout to expire even if the flip completes quickly.
    if (this.lockoutRemainingMs > 0) {
      this.lockoutRemainingMs = Math.max(0, this.lockoutRemainingMs - dtMs);
    }

    if (!this.transitioning || !this.transitionFrom || !this.transitionTo) return;

    this.transitionElapsed += dtMs;
    const t = Math.min(this.transitionElapsed / this.transitionDuration, 1);

    // Smooth ease-in-out
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    // Lerp lighting
    this.applyLightingBlend(this.transitionFrom, this.transitionTo, eased);

    // Lerp shadow zone scales
    const expandTarget = this.transitionTo === "midnight"
      ? OVERGROWN_CONSERVATORY.TIME.SHADOW_EXPAND_FACTOR
      : this.transitionTo === "dusk"
        ? 2.0
        : 1.0;

    const expandFrom = this.transitionFrom === "midnight"
      ? OVERGROWN_CONSERVATORY.TIME.SHADOW_EXPAND_FACTOR
      : this.transitionFrom === "dusk"
        ? 2.0
        : 1.0;

    const currentExpand = expandFrom + (expandTarget - expandFrom) * eased;

    for (const entry of this.shadowZones) {
      entry.mesh.scaling.set(
        entry.baseScale.x * currentExpand,
        entry.baseScale.y * currentExpand,
        entry.baseScale.z,
      );
    }

    // Complete
    if (t >= 1) {
      this.currentPhase = this.transitionTo;
      this.transitioning = false;
      this.transitionFrom = null;
      this.transitionTo = null;

      this.applyLighting(this.currentPhase);

      gameEvents.emit(GameEvents.DAY_NIGHT_CHANGED, {
        phase: this.currentPhase,
        isNight: this.isNight,
      });
      gameEvents.emit(GameEvents.CONSERVATORY_PHASE_CHANGED, {
        phase: this.canonicalPhase,
        isIncubation: this.isIncubation,
      });
    }
  }

  // ─── Lighting helpers ─────────────────────────────────────────

  private applyLighting(phase: TimeOfDay): void {
    const preset = this.presets[phase];
    this.hemiLight.diffuse = preset.ambient;
    this.hemiLight.groundColor = preset.ambient.scale(0.3);
    this.hemiLight.intensity = preset.intensity;
    this.dirLight.diffuse = preset.directional;
    this.dirLight.intensity = preset.intensity * 1.5;
  }

  private applyLightingBlend(from: TimeOfDay, to: TimeOfDay, t: number): void {
    const a = this.presets[from];
    const b = this.presets[to];

    this.hemiLight.diffuse = Color3.Lerp(a.ambient, b.ambient, t);
    this.hemiLight.groundColor = Color3.Lerp(
      a.ambient.scale(0.3),
      b.ambient.scale(0.3),
      t,
    );
    this.hemiLight.intensity = a.intensity + (b.intensity - a.intensity) * t;

    this.dirLight.diffuse = Color3.Lerp(a.directional, b.directional, t);
    this.dirLight.intensity =
      a.intensity * 1.5 + (b.intensity * 1.5 - a.intensity * 1.5) * t;
  }
}
